import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import clsx from "clsx";
import { memo, useEffect, useRef, useState, type ComponentProps } from "react";
import AppAssistenteIAContextoToggle from "./AppAssistenteIAContextoToggle";

const MAX_CARACTERES_PERGUNTA = 8000;

type FormSubmitHandler = NonNullable<ComponentProps<"form">["onSubmit"]>;
type TextFieldKeyDownHandler = NonNullable<ComponentProps<typeof TextField>["onKeyDown"]>;

type MessageComposerProps = {
	readonly aberto: boolean;
	readonly enviando: boolean;
	readonly limpando: boolean;
	readonly erro: string | null;
	readonly manterContexto: boolean;
	readonly onAlterarManterContexto: (valor: boolean) => void;
	readonly onEnviarMensagem: (conteudo: string) => void;
};

const MessageComposer = memo(function MessageComposer({
	aberto,
	enviando,
	limpando,
	erro,
	manterContexto,
	onAlterarManterContexto,
	onEnviarMensagem,
}: MessageComposerProps) {
	const [pergunta, setPergunta] = useState("");
	const perguntaInputRef = useRef<HTMLTextAreaElement | null>(null);
	const atingiuLimite = pergunta.length >= MAX_CARACTERES_PERGUNTA;

	useEffect(() => {
		if (!aberto) return;
		perguntaInputRef.current?.focus();
	}, [aberto]);

	function enviarPerguntaAtual() {
		const perguntaNormalizada = pergunta.trim();
		if (!perguntaNormalizada || enviando || limpando) return;

		onEnviarMensagem(perguntaNormalizada);
		setPergunta("");
	}

	const handleSubmit: FormSubmitHandler = (event) => {
		event.preventDefault();
		enviarPerguntaAtual();
	};

	const handlePerguntaKeyDown: TextFieldKeyDownHandler = (event) => {
		if (
			event.key !== "Enter" ||
			event.shiftKey ||
			event.ctrlKey ||
			event.altKey ||
			event.metaKey ||
			event.nativeEvent.isComposing
		)
			return;

		event.preventDefault();
		enviarPerguntaAtual();
	};

	return (
		<form className="shrink-0 bg-background p-3" onSubmit={handleSubmit} aria-busy={enviando || limpando}>
			<div className="mx-auto mb-2 flex w-full max-w-4xl">
				<AppAssistenteIAContextoToggle
					manterContexto={manterContexto}
					enviando={enviando || limpando}
					onAlterar={onAlterarManterContexto}
				/>
			</div>

			<div className="mx-auto grid w-full max-w-4xl grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 gap-y-1">
				<TextField
					value={pergunta}
					onChange={(event) => setPergunta(event.target.value)}
					placeholder="Pergunte sobre esta análise... (Shift+Enter para pular linha)"
					size="small"
					fullWidth
					multiline
					minRows={2}
					maxRows={6}
					disabled={enviando || limpando}
					onKeyDown={handlePerguntaKeyDown}
					inputRef={perguntaInputRef}
					slotProps={{ htmlInput: { maxLength: MAX_CARACTERES_PERGUNTA, "aria-label": "Sua pergunta" } }}
				/>
				<Tooltip title="Enviar" placement="top">
					<span className="inline-flex">
						<IconButton
							type="submit"
							color="secondary"
							aria-label="Enviar pergunta"
							disabled={!pergunta.trim() || enviando || limpando}
							className="h-10 w-10"
						>
							{enviando ? (
								<CircularProgress size={18} color="inherit" />
							) : (
								<FuseSvgIcon size={20}>lucide:send</FuseSvgIcon>
							)}
						</IconButton>
					</span>
				</Tooltip>
				<span
					className={clsx(
						"col-start-1 justify-self-end text-xs tabular-nums",
						atingiuLimite ? "text-warning" : "text-muted-foreground",
					)}
				>
					{pergunta.length}/{MAX_CARACTERES_PERGUNTA}
				</span>
			</div>

			{erro ? <p className="mx-auto mt-2 max-w-4xl text-xs text-warning">{erro}</p> : null}
		</form>
	);
});

export default MessageComposer;
